import type { User } from '@auth0/auth0-spa-js'
import React, { ReactNode } from 'react'
import { Auth0Provider } from '@auth0/auth0-react'

import {
  AUTH0_AUDIENCE,
  AUTH0_CLIENT_ID,
  AUTH0_DOMAIN,
  AUTH0_REDIRECT_URI,
} from './auth0-config'

function getWebOrigin(): string | undefined {
  if (typeof window === 'undefined') {
    return undefined
  }

  return window.location.origin
}

/** Must match the `/callback` route and Auth0 Application callback URLs. */
function getWebRedirectUri(): string {
  if (AUTH0_REDIRECT_URI != null && AUTH0_REDIRECT_URI !== '') {
    return AUTH0_REDIRECT_URI
  }

  const origin = getWebOrigin()
  if (origin != null) {
    return `${origin}/callback`
  }

  // Bundle may load outside a browser during tooling; real logins only run in the client.
  return 'http://localhost/callback'
}

export async function getAuth0UserInfo(accessToken: string): Promise<User> {
  const response = await fetch(`https://${AUTH0_DOMAIN}/userinfo`, {
    headers: {
      Authorization: `Bearer ${accessToken}`,
    },
  })

  if (!response.ok) {
    throw new Error(`Auth0 userinfo request failed with ${response.status}`)
  }

  return (await response.json()) as User
}

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  if (typeof window === 'undefined') {
    // Avoid initializing auth0-spa-js during SSR/tooling runs.
    return null
  }

  return (
    <Auth0Provider
      domain={AUTH0_DOMAIN}
      clientId={AUTH0_CLIENT_ID}
      authorizationParams={{
        audience: AUTH0_AUDIENCE,
        redirect_uri: getWebRedirectUri(),
        scope: 'openid profile email offline_access',
      }}
      cacheLocation="localstorage"
      useRefreshTokens={true}
      useRefreshTokensFallback={true}
      onRedirectCallback={(appState) => {
        const origin = getWebOrigin()
        const returnTo = appState?.returnTo
        if (origin == null || returnTo == null) {
          return
        }

        const nextUrl = new URL(returnTo, origin)
        if (window.location.pathname === nextUrl.pathname) {
          return
        }

        window.location.replace(nextUrl.toString())
      }}
    >
      {children}
    </Auth0Provider>
  )
}
